import { useEffect, useRef, useState } from 'react';
import { KB_DIALOG_TRANSITION } from './keyboardOverlayStyles';
import { splitTop } from '../../hooks/ui/modalMeasure';

/**
 * SINGLE OWNER of the dialog's `transition` on touch (one keyboard motion).
 * The keyboard list (transform / max-height / height on the iOS curve) comes
 * FIRST, the panel's own motion LAST so enter/exit keep their timing. Once the
 * enter has finished (`steady`, and still visible) the panel's `transform` entry
 * is dropped: a duplicate property resolves to the LAST entry and would
 * otherwise run the keyboard glide on the panel's timing. Exit (`!visible`)
 * always keeps the panel's own transform timing.
 *
 * `phase1` (curtain's first frame) drops ONLY the max-height entry, so the
 * shrink starts FROM the resting height while a running FLIP (transform) and
 * height tween survive. `instant` (a real width change = rotation, never while
 * the keyboard moves) is 'none' for one frame so it applies instead of tweening.
 * Morph states ('none') and reduced motion stay 'none'; desktop is unchanged.
 */
// A rotation pulse never runs while the keyboard is up or within this window
// after it opened/closed (KB_MOTION 250ms + 80): it would cancel running motions.
const KEYBOARD_QUIET_MS = 330;

export function useDialogSteady(visible, durationMs, preferMobile = true, keyboardOpen = false) {
  const [steady, setSteady] = useState(false);
  const [instant, setInstant] = useState(false);
  const kbRef = useRef({ open: keyboardOpen, changedAt: -Infinity });
  if (kbRef.current.open !== keyboardOpen) kbRef.current = { open: keyboardOpen, changedAt: Date.now() };
  useEffect(() => {
    if (!preferMobile) return undefined;
    if (!visible) { setSteady(false); return undefined; }
    const t = setTimeout(() => setSteady(true), durationMs + 60);
    return () => clearTimeout(t);
  }, [visible, durationMs, preferMobile]);
  // Rotation only (a real WIDTH change) — never the touch-reserve vars, which
  // move during keyboard open/close and must keep animating.
  useEffect(() => {
    if (!preferMobile || typeof window === 'undefined') return undefined;
    if (!visible) { setInstant(false); return undefined; }
    let width = window.innerWidth;
    let r1 = 0;
    let r2 = 0;
    const onResize = () => {
      if (window.innerWidth === width) return;
      width = window.innerWidth;
      const k = kbRef.current;
      if (k.open || Date.now() - k.changedAt < KEYBOARD_QUIET_MS) return;
      setInstant(true);
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
      r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setInstant(false)); });
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
      setInstant(false);
    };
  }, [visible, preferMobile]);
  return { steady, instant };
}

export function composeDialogTransition({
  preferMobile, reducedMotion, panelT, steady, visible = true, phase1 = false, instant = false,
}) {
  if (!preferMobile) return panelT;
  if (reducedMotion || panelT === 'none' || instant) return 'none';
  const kb = phase1
    ? splitTop(KB_DIALOG_TRANSITION).filter((e) => !/^max-height\b/.test(e)).join(', ')
    : KB_DIALOG_TRANSITION;
  if (!panelT) return kb;
  const panel = (steady && visible)
    ? splitTop(panelT).filter((e) => !/^transform\b/.test(e)).join(', ')
    : panelT;
  return panel ? `${kb}, ${panel}` : kb;
}
